/**
 * MessageCheckbox Component
 * Checkbox for selecting messages in chat
 * Shows tooltip when message can't be selected (wrong type or limit reached)
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, Ban } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';

const MessageCheckbox = ({ 
  isSelected, 
  isVisible, 
  onToggle,
  size = 22,
  isBlocked = false,
  isAtLimit = false,
  blockReason = '',
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const checkboxRef = useRef(null);

  const canSelect = !isBlocked && (!isAtLimit || isSelected);
  const showBlockedState = isBlocked || (isAtLimit && !isSelected);

  // Update tooltip position
  useEffect(() => {
    if (!showTooltip || !checkboxRef.current) return;

    const rect = checkboxRef.current.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, [showTooltip]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!canSelect && !isSelected) return;
    onToggle?.();
  };

  const tooltipText = isBlocked 
    ? blockReason || 'Apenas mensagens de texto podem ser selecionadas'
    : isAtLimit && !isSelected
      ? 'Limite de 5 mensagens atingido'
      : '';

  const checkboxStyles = {
    container: {
      width: 36,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.15s ease',
      flexShrink: 0,
      position: 'relative',
    },
    checkbox: {
      width: size,
      height: size,
      borderRadius: '50%',
      border: `2px solid ${
        isSelected 
          ? workspaceColors.accentPrimary 
          : workspaceColors.textTertiary
      }`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: canSelect || isSelected ? 'pointer' : 'not-allowed',
      transition: 'all 0.15s ease',
      backgroundColor: isSelected 
        ? workspaceColors.accentPrimary 
        : 'transparent',
      opacity: showBlockedState && !isSelected ? 0.4 : 1,
    },
  };

  const tooltipStyles = {
    container: {
      position: 'fixed',
      top: tooltipPosition.top,
      left: tooltipPosition.left,
      transform: 'translate(-50%, -100%)',
      backgroundColor: workspaceColors.bgPrimary,
      color: workspaceColors.textPrimary,
      padding: '8px 12px',
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 500,
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      border: `1px solid ${workspaceColors.borderColor}`,
      zIndex: 10000,
      pointerEvents: 'none',
      maxWidth: 220,
    },
    arrow: {
      position: 'absolute',
      bottom: -6,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderTop: `6px solid ${workspaceColors.borderColor}`,
    },
  };

  // Render tooltip via Portal
  const renderTooltip = () => {
    if (!showTooltip || !tooltipText) return null;

    return createPortal(
      <div style={tooltipStyles.container}>
        {tooltipText}
        <div style={tooltipStyles.arrow} />
      </div>,
      document.body
    );
  };

  return (
    <div 
      ref={checkboxRef}
      style={checkboxStyles.container}
      onMouseEnter={() => showBlockedState && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        style={checkboxStyles.checkbox}
        onClick={handleClick}
        role="checkbox"
        aria-checked={isSelected}
        aria-disabled={showBlockedState}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        {isSelected && (
          <Check 
            size={size * 0.6} 
            color="white" 
            strokeWidth={3} 
          />
        )}
        {showBlockedState && !isSelected && (
          <Ban 
            size={size * 0.5} 
            color={workspaceColors.textTertiary} 
            strokeWidth={2} 
          />
        )}
      </div>

      {/* Tooltip via Portal */}
      {renderTooltip()}
    </div>
  );
};

export default MessageCheckbox;